<template>
  <div class="ops-page">
    <section class="content-card">
      <div class="section-heading">
        <h2>任务执行日志</h2>
        <div class="intro-actions">
          <span class="live-state" :class="{ 'sync-error': logSyncError, 'sync-paused': !isLogPolling }"><i
              class="live-dot" />{{ logSyncError ?
                '日志同步异常' : isLogPolling ? '日志实时同步中' : '任务已停止 · 自动同步暂停' }}</span>
          <el-button type="primary" icon="el-icon-download" @click="exportLogs">导出日志</el-button>
        </div>
      </div>
      <el-table :data="pagedLogs" class="log-table" style="width: 100%" :empty-text="'没有找到匹配的日志'">
        <el-table-column prop="source" label="来源" width="130" />
        <el-table-column prop="time" label="时间" width="180" />
        <el-table-column prop="content" label="日志内容" min-width="320" show-overflow-tooltip />
      </el-table>
      <div class="table-footer"><span>显示 {{ rangeStart }}–{{ rangeEnd }} 条，共 {{ filteredLogs.length }}
          条</span><el-pagination background layout="prev, pager, next" :total="filteredLogs.length"
          :page-size="pageSize" :current-page.sync="currentPage" /></div>
    </section>
  </div>
</template>

<script>
import { getLogList } from '@/request/api'
import { getTaskStatuses, RECRUITMENT_TASK_STATUS_EVENT } from '@/utils/bossTask'
import { cacheLogs, readCachedLogs } from '@/utils/pendingLog'

const initialLogs = [
  // { source: 'BOSS', time: '2026-10-10 10:46:11', content: '（PHP工程师）该候选人简历合适，已进入简历收藏' },
  // { source: '智联', time: '2026-10-10 10:45:32', content: '（爬虫工程师）不符合！候选人专业为电子信息科学与技术，不属于计算机类专业' },
  // { source: '猎聘', time: '2026-10-10 10:44:30', content: '（算法工程师）正在判断王帅基本信息' },
  // { source: '猎聘', time: '2026-10-10 10:44:30', content: '（算法工程师）正在判断王帅基本信息' },
  // { source: '猎聘', time: '2026-10-10 10:44:30', content: '（算法工程师）正在判断王帅基本信息' },
  // { source: '猎聘', time: '2026-10-10 10:44:30', content: '（算法工程师）正在判断王帅基本信息' },
  // { source: '猎聘', time: '2026-10-10 10:44:30', content: '（算法工程师）正在判断王帅基本信息' },
  // { source: '猎聘', time: '2026-10-10 10:44:30', content: '（算法工程师）正在判断王帅基本信息' },
  // { source: '猎聘', time: '2026-10-10 10:44:30', content: '（算法工程师）正在判断王帅基本信息' },
  // { source: '系统事件', time: '2026-10-10 10:43:49', content: '（测试工程师）启动BOSS直聘，等待用户完成登录' },
]

export default {
  data() {
    return {
      logs: initialLogs.slice(),
      currentPage: 1,
      pageSize: 8,
      logPollTimer: null,
      isLogPolling: false,
      isFetchingLogs: false,
      logSyncError: false
    }
  },
  created() {
    this.mergeLogs(readCachedLogs())
    const pendingEntry = sessionStorage.getItem('pendingLogEntry')
    if (pendingEntry) {
      sessionStorage.removeItem('pendingLogEntry')
      try {
        const parsedEntries = JSON.parse(pendingEntry)
        const entries = (Array.isArray(parsedEntries) ? parsedEntries : [parsedEntries])
          .filter(entry => entry && entry.source && entry.time && entry.content)
        if (entries.length) this.mergeLogs(entries)
      } catch (error) {
        // 忽略无效的临时日志数据，继续展示已有日志。
      }
    }
    window.addEventListener(RECRUITMENT_TASK_STATUS_EVENT, this.syncLogPolling)
    window.addEventListener('storage', this.syncLogPolling)
    this.fetchLogs()
    this.syncLogPolling()
  },
  beforeDestroy() {
    window.removeEventListener(RECRUITMENT_TASK_STATUS_EVENT, this.syncLogPolling)
    window.removeEventListener('storage', this.syncLogPolling)
    this.stopLogPolling()
  },
  computed: {
    filteredLogs() { return this.logs },
    pagedLogs() { const start = (this.currentPage - 1) * this.pageSize; return this.filteredLogs.slice(start, start + this.pageSize) },
    rangeStart() { return this.filteredLogs.length ? (this.currentPage - 1) * this.pageSize + 1 : 0 },
    rangeEnd() { return Math.min(this.currentPage * this.pageSize, this.filteredLogs.length) }
  },
  methods: {
    syncLogPolling() {
      const statuses = getTaskStatuses()
      const hasRunningTask = Object.keys(statuses).some(channel => Boolean(statuses[channel]))
      if (hasRunningTask && !this.logPollTimer) {
        this.logPollTimer = setInterval(this.fetchLogs, 3000)
      } else if (!hasRunningTask) {
        this.stopLogPolling()
      }
      this.isLogPolling = hasRunningTask
    },
    stopLogPolling() {
      if (this.logPollTimer) {
        clearInterval(this.logPollTimer)
        this.logPollTimer = null
      }
      this.isLogPolling = false
    },
    async fetchLogs() {
      if (this.isFetchingLogs) return
      this.isFetchingLogs = true
      try {
        const { data: response } = await getLogList()
        if (!response || response.code !== 200 || !Array.isArray(response.data)) {
          this.logSyncError = true
          return
        }
        this.logSyncError = false
        this.mergeLogs(response.data)
      } catch (error) {
        this.logSyncError = true
      } finally {
        this.isFetchingLogs = false
      }
    },
    mergeLogs(entries) {
      const knownKeys = new Set(this.logs.map(this.logIdentity))
      const incoming = []
      entries.forEach(entry => {
        if (!entry || !entry.source || !entry.time || !entry.content) return
        const normalized = {
          id: entry.id,
          source: String(entry.source),
          time: String(entry.time),
          content: String(entry.content)
        }
        const key = this.logIdentity(normalized)
        if (knownKeys.has(key)) return
        knownKeys.add(key)
        incoming.push(normalized)
      })
      if (!incoming.length) return

      this.logs = [...incoming, ...this.logs].sort((left, right) => this.logTimestamp(right.time) - this.logTimestamp(left.time))
      cacheLogs(this.logs)
    },
    logIdentity(entry) {
      return entry.id !== undefined && entry.id !== null
        ? `id:${entry.id}`
        : `${entry.source}|${entry.time}|${entry.content}`
    },
    logTimestamp(time) {
      const timestamp = new Date(String(time).replace(/-/g, '/')).getTime()
      return Number.isNaN(timestamp) ? 0 : timestamp
    },
    exportLogs() { const header = ['来源', '时间', '日志内容']; const rows = this.filteredLogs.map(r => [r.source, r.time, r.content]); const csv = [header, ...rows].map(row => row.map(v => '"' + String(v).replace(/"/g, '""') + '"').join(',')).join('\r\n'); const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' }); const url = URL.createObjectURL(blob); const link = document.createElement('a'); link.href = url; link.download = '系统运行日志.csv'; link.click(); URL.revokeObjectURL(url); this.$message.success('日志导出成功') }
  }
}
</script>

<style lang="scss" scoped>
.ops-page {
  max-width: 1440px;
  margin: 0 auto;
}

.intro-actions {
  display: flex;
  align-items: center;
  gap: 10px;

  .live-state {
    display: flex;
    align-items: center;
    gap: 7px;
    margin-right: 8px;
    color: var(--text-secondary);
    font-size: 12px;
  }
}

.live-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--success);
  box-shadow: 0 0 0 3px rgba(30, 170, 120, .12);
}

.live-state.sync-error .live-dot {
  background: var(--warning);
  box-shadow: 0 0 0 3px rgba(223, 155, 50, .12);
}

.live-state.sync-paused:not(.sync-error) .live-dot {
  background: var(--text-disabled);
  box-shadow: 0 0 0 3px rgba(128, 138, 153, .12);
}

.content-card {
  background: var(--bg-card);
  border: 1px solid var(--border-hairline);
  border-radius: 12px;
  box-shadow: var(--shadow-card);
}

.content-card {
  padding: 20px 22px 12px;
}

.section-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;

  h2 {
    color: var(--text-primary);
    font-size: 16px;
    line-height: 1.3;
  }
}

.log-table {
  color: var(--text-regular);
}

.table-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 16px;
  color: var(--text-secondary);
  font-size: 12px;
}

@media (max-width: 1000px) {
  .intro-actions {
    flex-wrap: wrap;
  }
}

@media (max-width: 640px) {
  .section-heading {
    align-items: flex-start;
    flex-direction: column;
  }

  .content-card {
    padding: 16px 12px;
  }

  .table-footer {
    align-items: flex-start;
    flex-direction: column;
    gap: 12px;
  }
}
</style>
